/*
* required: 必填
* minMaxLength 最小最大 校验
*/
import {isObject, isNull, isUndefined, isArray, isEmpty, isEqual, isNumber} from 'lodash-es'
import NP from 'number-precision'
import {FOLLOW_SETTING_LIST, FIELD_TYPE} from '../../constants/index'
import response from '../../../utils/response'
import i18n from './i18n.js'
import Api from './api'

const pageText = window.fe.getPageText(i18n)
const variableReplace = window.fe.variableReplace

// 组件内部配置 可以覆盖公共配置 使用数组定义规则为了保证校验顺序
const fieldRule = {
  'shortText': [
    'required',
    'minMaxLength',
    'unique'
  ],
  'textArea': [
    'required',
    'minMaxLength',
    'unique'
  ],
  'singleChoice': [
    'required',
    'unique'
  ],
  'multipleChoices': [
    'required',
    'unique'
  ],
  'quote': [
    'required'
  ],
  'percentage': [
    'required',
    'isNamber',
    'percentageLength',
    'digitalSize',
    'unique'
  ],
  'number': [
    'required',
    'isNamber',
    'numberLength',
    'digitalSize',
    'unique'
  ],
  'currency': [
    'required',
    'isNamber',
    'numberLength',
    'digitalSize',
    'unique'
  ],
  'date': [
    'required',
    'unique'
  ],
  'dateTime': [
    'required',
    'unique'
  ],
  'period': [
    'required',
    'periodReuqired',
    'dateComparison'
  ],
  'lookupRelationship': [
    'required',
    'unique'
  ],
  'department': [
    'required',
    'unique'
  ],
  'personnel': [
    'required',
    'unique'
  ],
  'image': [
    'required',
    'maxUploadNum',
    'upload'
  ],
  'file': [
    'required',
    'maxUploadNum',
    'upload'
  ],
  'default': [
    'required'
  ]
}

// 不同字段类型的校验回调
const uniqueCheckCallback = ({field, list = [], value}) => new Promise((resolve, reject) => {
  const {_type, apiName} = field
  const stringCheckHandle = () => {
    if (list.filter(v => v[apiName] === value).length > 1) {
      return new Error(pageText.currentValueDuplicate)
    }
  }
  const arrayCheckHandle = () => {
    if (list.filter(v => isEqual(v[apiName], value)).length > 1) {
      return new Error(pageText.currentValueDuplicate)
    }
  }
  const fieldMap = {
    'shortText': stringCheckHandle,
    'textArea': stringCheckHandle,
    'singleChoice': stringCheckHandle,
    'multipleChoices': arrayCheckHandle,
    'percentage': stringCheckHandle,
    'number': stringCheckHandle,
    'currency': stringCheckHandle,
    'date': stringCheckHandle,
    'lookupRelationship': stringCheckHandle
  }

  const fieldError = fieldMap[_type] && fieldMap[_type]()
  fieldError && fieldError.name === 'Error'
    ? reject(fieldError.message)
    : resolve()
})

const allRule = {
  required: function (field) {
    let attr = field.attr || {}
    let ruleConfig = attr.ruleConfig || {}
    let requieRule = (ruleConfig.rules && ruleConfig.rules.required) || {}
    let trigger = ruleConfig.trigger || requieRule.trigger
    let msg = `${pageText.pleaseEnter}${field.name}`
    if (requieRule && requieRule.msg) {
      msg = requieRule.msg(field)
    }
    const validatorFun = function (rule, value, callback) {
      // 必填校验 先校验是否是必填字段
      if (rule.required) {
        // 处理 _update逻辑
        if (!isEmpty(value) && Object.prototype.toString.call(value) === '[object Object]') {
          const _arr = Object.keys(value)
          if (_arr.length === 1 && _arr[0] === '_update') {
            callback(new Error(msg))
          }
        }
        if (
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          (typeof value === 'string' && value) ||
          (isObject(value) && !isEmpty(value))
        ) {
          callback()
        } else {
          callback(new Error(''))
        }
      } else {
        callback()
      }
    }
    const rule = {
      rule: {
        required: field.required || (field.fieldConf && field.fieldConf.required) || field.subObjectInfo?.fromObjectRequired,
        message: msg,
        validator: ruleConfig.rules?.required?.validator || validatorFun,
        trigger: trigger
      },
      isOperant: true
    }
    if (requieRule && requieRule.fieldDataType) {
      rule.rule.type = requieRule.fieldDataType(field)
    }
    return rule
  },
  minMaxLength: function (field) {
    let minLength = field.minLength
    let maxLength = field.maxLength
    let ruleConfig = field.attr.ruleConfig
    return {
      rule: {
        validator: function (rule, value, callback) {
          if (isEmpty(value)) {
            return callback()
          }
          if (String(value).length < minLength || String(value).length > maxLength) {
            return callback(new Error(
              `${variableReplace(pageText.minMaxLength, {
                name: field.name,
                minLength: minLength,
                maxLength: maxLength
              })}`
            ))
          }
          callback()
        },
        trigger: ruleConfig.trigger
      },
      isOperant: minLength && maxLength
    }
  },
  isNamber: function (field) {
    let ruleConfig = field.attr.ruleConfig || {}
    return {
      rule: {
        validator: function (rule, value, cb) {
          if (isEmpty(value)) {
            return cb()
          }
          if (new RegExp(/^(-?\d+(\.\d+$|$)|$)/).test(value)) {
            return cb()
          }

          cb(new Error(pageText.pleaseEnterTheNumber))
        },
        trigger: ruleConfig.trigger
      },
      isOperant: true
    }
  },
  numberLength: function (field) {
    const {
      followSetting,
      _type,
      systemDecimalMaxDigits,
      decimalMaxDigits,
      attr
    } = field
    const getMaxDigits = () => {
      const systemMaxDigits = ({
        'number': {
          [FOLLOW_SETTING_LIST.SYSTEM]: systemDecimalMaxDigits,
          [FOLLOW_SETTING_LIST.FIELD]: decimalMaxDigits,
          [FOLLOW_SETTING_LIST.CURRENCY]: decimalMaxDigits
        },
        'currency': {
          [FOLLOW_SETTING_LIST.SYSTEM]: systemDecimalMaxDigits,
          [FOLLOW_SETTING_LIST.FIELD]: decimalMaxDigits,
          [FOLLOW_SETTING_LIST.CURRENCY]: decimalMaxDigits
        }
      })[_type]?.[followSetting]

      return isNumber(systemMaxDigits) ? systemMaxDigits : decimalMaxDigits
    }

    return {
      rule: {
        validator: function (rule, value, cb) {
          let integerMaxDigits = field.integerMaxDigits || 1
          let maxDigits = getMaxDigits() || 0
          let re = new RegExp('^(\\-?\\d{1,' + integerMaxDigits + '}$|$)')

          if (isEmpty(value)) {
            return cb()
          }

          if (maxDigits > 0) {
            if (new RegExp('^(\\-?\\d{1,' + integerMaxDigits + '}(\\.\\d{1,' + maxDigits + '}$|$)|$)').test(value)) {
              return cb()
            }
          }

          if (re.test(value)) {
            return cb()
          }
          cb(new Error(`${variableReplace(pageText.numberLength, {
            integerMaxDigits: integerMaxDigits,
            decimalMaxDigits: maxDigits
          })}`))
        },
        trigger: attr.ruleConfig.trigger
      },
      isOperant: true
    }
  },
  digitalSize: function (field) {
    let ruleConfig = field.attr.ruleConfig || {}
    
    function checkDigitalSize (rule, value, callback) {
      if (isEmpty(value)) {
        return callback()
      }
      let maxValue = Number(field.maxValue)
      let minValue = Number(field.minValue)
      let _value = Number(value)
      if (field.type === FIELD_TYPE.PERCENTAGE && !field.props.percentileValue) {
        maxValue = NP.times(maxValue, 100)
        minValue = NP.times(minValue, 100)
        _value = NP.times(_value, 100)
      }
      if (field.containMinValue && _value < minValue) {
        return callback(new Error(`${variableReplace(pageText.lessThanOrEqualToTheMinimum, {minValue: minValue})}`))
      } else if (!field.containMinValue && _value <= minValue) {
        return callback(new Error(`${variableReplace(pageText.lessThanTheMinimum, {minValue: minValue})}`))
      }
      if (field.containMaxValue && _value > maxValue) {
        return callback(new Error(`${variableReplace(pageText.greaterThanOrEqualToTheMaximum, {maxValue: maxValue})}`))
      } else if (!field.containMaxValue && _value >= maxValue) {
        return callback(new Error(`${variableReplace(pageText.greaterThanTheMaximum, {maxValue: maxValue})}`))
      }
      return callback()
    }

    return {
      rule: {validator: checkDigitalSize, trigger: ruleConfig.trigger},
      isOperant: true
    }
  },
  percentageLength: function (field) {
    let ruleConfig = field.attr.ruleConfig || {}
    return {
      rule: {
        pattern: new RegExp('^\\-?\\d{1,12}(\\.\\d{1,10}$|$)'),
        message: pageText.integersAreAllowedToHaveMaximum,
        trigger: ruleConfig.trigger
      },
      isOperant: true
    }
  },
  upload: function (field) {
    let ruleConfig = field.attr.ruleConfig || {}

    function checkUploadState (rule, value, callback) {
      if (!value || !value.length) {
        callback()
        return true
      }
      let uploadEnd = value.every(item => {
        // 考虑正在上传中 和 上传队列中等待上传状态的文件
        return item.status !== 'uploading' && item.status !== 'ready'
      })
      uploadEnd ? callback() : callback(new Error(field.type === 'image' ? pageText.theImgIsStillBeingUploaded : pageText.theFileIsStillBeingUploaded))
    }

    return {
      rule: {validator: checkUploadState, trigger: ruleConfig.trigger},
      isOperant: true
    }
  },
  // 上传文件最大数量限制
  maxUploadNum (field) {
    const {maxUploadNum, maxSize, format} = field
    const ruleConfig = field.attr.ruleConfig || {}
    const checkFieldMaximum = (size) => {
      const unit = 1024
      return maxSize && (size / unit / unit) > maxSize
    }
    // format = format.toLocaleLowerCase()
    const checkMaxUploadNum = function (rule, value = [], callback) {
      // 校验文件的最大可上传数量
      if (value.length > maxUploadNum) {
        return callback(new Error(`${variableReplace(pageText.MaximumAllowedUpload, {
          maxUploadNum
        })}`))
      }
      for (let v of value) {
        // 校验文件的上传大小
        if (checkFieldMaximum(v.size)) {
          return callback(new Error(`${variableReplace(pageText.FileSelectionPleaseReselectMaximumSupportUploading, {
            maxSize
          })}`))
        }
        // 校验文件格式
        let fileType = v._fileType && v._fileType.toLocaleLowerCase()
        if (fileType && format && !format.includes(fileType)) {
          return callback(new Error(`${variableReplace(pageText.FileSelectionErrorPleaseReselectOnlySupport, {
            fileType: format.join(',')
          })}`))
        }
      }
      callback()
    }

    return {
      rule: {validator: checkMaxUploadNum, trigger: ruleConfig.trigger},
      isOperant: true
    }
  },
  // 起止时间必填
  periodReuqired: function (field) {
    let ruleConfig = field.attr.ruleConfig || {}
    function checkPeriod (rule, value, callback) {
      if (rule.required) {
        if (!value.startTime) {
          return callback(new Error(pageText.pleaseSelectStartDate))
        } else if (!value.permanentSet && !value.endTime) {
          return callback(new Error(pageText.pleaseSelectEndDate))
        }
      }
      callback()
    }

    return {
      rule: {validator: checkPeriod, trigger: ruleConfig.trigger},
      isOperant: true
    }
  },
  // 起止时间 value 开始时间不能大于结束时间
  dateComparison: function (field) {
    function checkDateValue (rule, value, callback) {
      if (!value) {
        callback()
        return true
      }
      if (!value.startTime && !value.endTime && !value.permanentSet) {
        callback()
        return true
      }
      if (value.startTime && (!value.permanentSet && !value.endTime)) {
        return callback(new Error(pageText.pleaseSelectEndDate))
      } else if (!value.startTime && (value.permanentSet || value.endTime)) {
        return callback(new Error(pageText.pleaseSelectStartDate))
      }
      if (!value.permanentSet) {
        // 格式是 时分秒的时候 不允许 开始结束时间相等
        if (field.dateFormat === 'yyyy-MM-dd HH:mm:ss') {
          if (new Date(value.startTime).getTime() >= new Date(value.endTime).getTime()) {
            return callback(new Error(pageText.theEndTimeCannotBeLessThanTheStartTime))
          }
        } else {
          if (new Date(value.startTime).getTime() > new Date(value.endTime).getTime()) {
            return callback(new Error(pageText.theEndTimeCannotBeLessThanTheStartTime))
          }
        }
      }
      callback()
    }

    return {
      rule: {validator: checkDateValue, trigger: ['change']},
      isOperant: true
    }
  },
  unique: function (field) {
    let {ruleConfig, submitValue} = field.attr || {}
    function checkUniqueValidator (data = {}) {
      const list = data?.subData || []
      async function checkUnique (rule, value, callback) {
        let api = Api.valueUnique
        if (value === '' || isNull(value) || isUndefined(value) || (isArray(value) && value.length === 0) || (isObject(value) && isEmpty(value))) {
          return callback()
        }

        try {
          await uniqueCheckCallback({field, list, value, callback})
        } catch (e) {
          return callback(new Error(e))
        }

        const { row, formData } = data
        const sequenceId = row ? row.aPaaS_SequenceId : formData.aPaaS_SequenceId
        const id = String(sequenceId).includes(`_CREATE_`) ? '' : sequenceId
        const parameter = {
          layoutApiName: field.layoutApiName,
          objectApiName: field.uniqueApiName,
          tenantType: field.tenantType,
          aPaaS_SequenceId: id,
          dataValue: submitValue ? submitValue(value, field) : value,
          buttonApiName: 'check',
          fieldApiNAme: field.apiName,
          fieldSeqCode: field.sequenceCode
        }
        const res = await response({
          method: api.type,
          url: api.url,
          params: parameter
        })
        if (res.isOk) {
          if (res.content) {
            callback()
          } else {
            callback(new Error(`${pageText.thereHasBeenSuch}${field.name}`))
          }
        }
      }

      return { validator: checkUnique, trigger: ruleConfig.trigger }
    }
    return {
      rule: checkUniqueValidator,
      isOperant: (!field.repeated) && (field.targetType === 'field')
    }
  }
}

function generateRule (rules = [], field) {
  let fieldRule = []
  rules.forEach(item => {
    let obj = allRule[item](field)
    if (obj.isOperant) {
      fieldRule.push(obj.rule)
    }
  })
  return fieldRule
}

function getFieldRule (field) {
  const fieldType = field._type
  const rules = fieldRule[fieldType] || fieldRule['default']
  return generateRule(rules, field)
}

function createdSubRules (slaveRule = {}) {
  let rules = []
  if (slaveRule.fromObjectRequired) {
    let msg = slaveRule.verificationTips || `从对象数据为空`
    rules = [
      {
        required: true,
        validator: function (rule, value, callback) {
          if (value.length === 0) {
            return callback(new Error(msg))
          }
          return callback()
        }
      }
    ]
  }
  return rules
}

export {
  getFieldRule,
  createdSubRules
}
